import { arrivalNoticeStrings, deliveryDestinationOptions } from '@data/arrivalNotice';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import type { AdminInquiryDetail } from '@models/BookingInquiry';
import { DeliveryRequestPickupAddress } from '@models/next/DeliveryRequestPickupAddress';
import { PaymentDetails, PaymentSlip } from '@models/next/PaymentSlip';
import type { PreAlertDetails } from '@models/next/PreAlert';
import type { BaseUser } from '@models/User';
import { MessagesEither, firstLeftEither, makeMessagesEitherLeftIf } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const ArrivalNoticePaymentDetails = PaymentDetails.extend({
  total_amount: z.number(),
});

export const ArrivalNoticePaymentInfo = z.object({
  invoice_date: PaymentSlip.shape.invoice_date,
  invoice_no: PaymentSlip.shape.invoice_no,
  payment_amount: z.number(),
  payment_tax: z.number(),
  payment_details_count: z.number(),
  payment_details: ArrivalNoticePaymentDetails.array(),
});

export type ArrivalNoticePaymentInfo = z.infer<typeof ArrivalNoticePaymentInfo>;

export const ArrivalNoticeDeliveryDestionation = z.object({
  ...DeliveryRequestPickupAddress.shape,
  delivery_destination: DeliveryRequestPickupAddress.shape.delivery_destination.label(
    arrivalNoticeStrings.delivery_destination,
  ),
});

export type ArrivalNoticeDeliveryDestionation = z.infer<typeof ArrivalNoticeDeliveryDestionation>;

export const ArrivalNotice = z.object({
  ...ArrivalNoticeDeliveryDestionation.shape,
  sea_delivery_destination_code: z
    .string()
    .label(arrivalNoticeStrings.sea_delivery_destination_code),
  id: z.number(),
  vessel_schedule: z.number(),
  booking_inquiry: z.number(),
  house_bl_number: z.string().label(arrivalNoticeStrings.house_bl_number),
  shipper: z.string().label(arrivalNoticeStrings.shipper),
  consignee: z.string().label(arrivalNoticeStrings.consignee),
  shipping_mark: z.string().label(arrivalNoticeStrings.shipping_mark),
  vessel: z.string().label(arrivalNoticeStrings.vessel),
  voyage: z.string().label(arrivalNoticeStrings.voyage),
  pod: z.string().label(arrivalNoticeStrings.pod),
  pol: z.string().label(arrivalNoticeStrings.pol),
  eta: f.dateString().label(arrivalNoticeStrings.eta),
  etd: f.dateString().label(arrivalNoticeStrings.etd),
  commodity: z.string().label(arrivalNoticeStrings.commodity),
  nos: z.number().label(arrivalNoticeStrings.nos),
  packages: z.string().label(arrivalNoticeStrings.packages),
  gross_weight: f.decimal().label(arrivalNoticeStrings.gross_weight),
  measurement: f.decimal().label(arrivalNoticeStrings.measurement),
  overseas_local_amount: f.decimal().label(arrivalNoticeStrings.overseaLocalAmount),
  d2d_taxable_amount: f.decimal().label(arrivalNoticeStrings.d2d_taxable_amount),
  payments_terms: z.string(),
  exchange_rate: f.decimal().nullish(),
  is_locked: z.boolean(),
  payment_info: ArrivalNoticePaymentInfo.nullish().label(arrivalNoticeStrings.payment_info),
  updated_at: z.string(),
  created_at: z.string(),
});

export type ArrivalNotice = z.infer<typeof ArrivalNotice>;

export const PostArrivalNotice = z.object({
  booking_inquiry: ArrivalNotice.shape.booking_inquiry,
});

export type PostArrivalNotice = z.infer<typeof PostArrivalNotice>;

export const PutArrivalNotice = z.object({
  shipper: ArrivalNotice.shape.shipper,
  consignee: ArrivalNotice.shape.consignee,
  shipping_mark: ArrivalNotice.shape.shipping_mark,
  commodity: ArrivalNotice.shape.commodity,
  nos: ArrivalNotice.shape.nos,
  packages: ArrivalNotice.shape.packages,
  gross_weight: ArrivalNotice.shape.gross_weight,
  measurement: ArrivalNotice.shape.measurement,
  d2d_taxable_amount: ArrivalNotice.shape.d2d_taxable_amount,
  delivery_destination: ArrivalNotice.shape.delivery_destination,
  delivery_destination_code: ArrivalNotice.shape.delivery_destination_code,
  repository: ArrivalNotice.shape.repository,
  address: ArrivalNotice.shape.address,
  tel: ArrivalNotice.shape.tel,
  fax: ArrivalNotice.shape.fax,
  principal_name: ArrivalNotice.shape.principal_name,
});

export type PutArrivalNotice = z.infer<typeof PutArrivalNotice>;

export const PatchArrivalNotice = z.object({
  detail: z.string().label(formStrings.changeReason),
  shipper: ArrivalNotice.shape.shipper.optional(),
  consignee: ArrivalNotice.shape.consignee.optional(),
  shipping_mark: ArrivalNotice.shape.shipping_mark.optional(),
  commodity: ArrivalNotice.shape.commodity.optional(),
  nos: ArrivalNotice.shape.nos.optional(),
  packages: ArrivalNotice.shape.packages.optional(),
  gross_weight: ArrivalNotice.shape.gross_weight.optional(),
  measurement: ArrivalNotice.shape.measurement.optional(),
  d2d_taxable_amount: ArrivalNotice.shape.d2d_taxable_amount.optional(),
  delivery_destination: ArrivalNotice.shape.delivery_destination.optional(),
  delivery_destination_code: ArrivalNotice.shape.delivery_destination_code.optional(),
  sea_delivery_destination_code: ArrivalNotice.shape.sea_delivery_destination_code.optional(),
  payment_info: ArrivalNotice.shape.payment_info,
  repository: ArrivalNotice.shape.repository.optional(),
  address: ArrivalNotice.shape.address.optional(),
  tel: ArrivalNotice.shape.tel.optional(),
  fax: ArrivalNotice.shape.fax.optional(),
  principal_name: ArrivalNotice.shape.principal_name.optional(),
  is_locked: ArrivalNotice.shape.is_locked.optional(),
});

export type PatchArrivalNotice = z.infer<typeof PatchArrivalNotice>;

export const ArrivalNoticeForm = z.object({
  action: z.enum(['preview', 'submit', 'edit']),
  house_bl_number: ArrivalNotice.shape.house_bl_number,
  shipper: ArrivalNotice.shape.shipper.min(1),
  consignee: ArrivalNotice.shape.consignee.min(1),
  shipping_mark: ArrivalNotice.shape.shipping_mark.min(1),
  vessel: ArrivalNotice.shape.vessel,
  voyage: ArrivalNotice.shape.voyage,
  pod: ArrivalNotice.shape.pod,
  pol: ArrivalNotice.shape.pol,
  eta: ArrivalNotice.shape.eta,
  etd: ArrivalNotice.shape.etd,
  commodity: ArrivalNotice.shape.commodity.min(1),
  nos: ArrivalNotice.shape.nos,
  packages: ArrivalNotice.shape.packages,
  gross_weight: ArrivalNotice.shape.gross_weight,
  measurement: ArrivalNotice.shape.measurement,
  overseas_local_amount: ArrivalNotice.shape.overseas_local_amount,
  d2d_taxable_amount: ArrivalNotice.shape.d2d_taxable_amount,
  delivery_destination: ArrivalNoticeDeliveryDestionation.shape.delivery_destination.min(1),
  delivery_destination_code: ArrivalNotice.shape.delivery_destination_code,
  repository: ArrivalNoticeDeliveryDestionation.shape.repository,
  address: ArrivalNoticeDeliveryDestionation.shape.address,
  tel: ArrivalNoticeDeliveryDestionation.shape.tel,
  fax: ArrivalNoticeDeliveryDestionation.shape.fax,
  principal_name: ArrivalNoticeDeliveryDestionation.shape.principal_name,
  sea_delivery_destination: z.string().label(arrivalNoticeStrings.sea_delivery_destination),

  // Patch only
  detail: PatchArrivalNotice.shape.detail,
});

export type ArrivalNoticeForm = z.infer<typeof ArrivalNoticeForm>;

export class ArrivalNoticeHelper {
  static isFormValid(arrivalNotice: ArrivalNotice) {
    return ArrivalNoticeForm.safeParse(this.mapToForm(arrivalNotice)).success;
  }

  static mapToForm(arrivalNotice: ArrivalNotice): ArrivalNoticeForm {
    return {
      ...arrivalNotice,
      action: 'preview',
      detail: '',
      sea_delivery_destination:
        deliveryDestinationOptions.find(
          (option) =>
            option.delivery_destination_code === arrivalNotice.sea_delivery_destination_code,
        )?.delivery_destination ?? '',
    };
  }

  static canLockForConsistentDeliveryDestination(arrivalNotice: ArrivalNotice): MessagesEither {
    return makeMessagesEitherLeftIf(
      !!arrivalNotice.sea_delivery_destination_code &&
        arrivalNotice.delivery_destination_code !== arrivalNotice.sea_delivery_destination_code,
      {
        messages: [arrivalNoticeStrings.errorInconsistentDeliveryDestination],
      },
    );
  }

  static canLockWhen(preAlert: PreAlertDetails, arrivalNotice: ArrivalNotice): MessagesEither {
    return firstLeftEither(
      () => AdminInquiryDetailHelper.canExportArrivalNotice(preAlert),
      () =>
        makeMessagesEitherLeftIf(arrivalNotice.is_locked, {
          messages: [arrivalNoticeStrings.errorAlreadyLocked],
        }),
      () => this.canLockForConsistentDeliveryDestination(arrivalNotice),
      () =>
        makeMessagesEitherLeftIf(
          !arrivalNotice.sea_delivery_destination_code &&
            arrivalNotice.delivery_destination !== 'その他',
          {
            messages: [arrivalNoticeStrings.errorNotOtherDeliveryDestination],
          },
        ),
    );
  }

  static canLock(
    user: BaseUser,
    bookingInquiry: AdminInquiryDetail,
    preAlert: PreAlertDetails,
    arrivalNotice: ArrivalNotice,
  ): MessagesEither {
    return firstLeftEither(
      () => new AdminInquiryDetailHelper(bookingInquiry).canEdit(user, preAlert),
      () => this.canLockWhen(preAlert, arrivalNotice),
    );
  }

  static shouldDisplayPaymentInfo(arrivalNotice: ArrivalNotice): boolean {
    return (
      arrivalNotice.is_locked &&
      (!!arrivalNotice.sea_delivery_destination_code
        ? this.shouldHavePaymentInfo(arrivalNotice)
        : arrivalNotice.delivery_destination === 'その他')
    );
  }

  static shouldHavePaymentInfo(arrivalNotice: ArrivalNotice): boolean {
    return deliveryDestinationOptions.some(
      (o) =>
        (o.type === 'FC A/N自社倉庫' || o.type === 'FC A/N契約倉庫') &&
        o.delivery_destination_code === arrivalNotice.delivery_destination_code,
    );
  }
}

export const exampleArrivalNoticePaymentInfo: ArrivalNoticePaymentInfo = {
  invoice_date: '2023-03-01',
  invoice_no: 'AAABBCCCC8964',
  payment_amount: 600,
  payment_details_count: 2,
  payment_tax: 0,
  payment_details: [
    {
      id: 1,
      name: '妄想税',
      memo: 'MEMOMEMO',
      unit_price: 300,
      unit_num: 1,
      excise_tax: 30,
      excise_tax_type: '課税10%',
      cost_id: 1,
      unit_num_type: 'B/L',
      total_amount: 300,
      debit: '支払在庫',
      credit: '買掛金',
    },
    {
      id: 1,
      name: '妄想税',
      memo: 'MEMOMEMO',
      unit_price: 300,
      unit_num: 1,
      excise_tax: 30,
      excise_tax_type: '課税10%',
      cost_id: 1,
      unit_num_type: 'B/L',
      total_amount: 300,
      debit: '支払在庫',
      credit: '買掛金',
    },
  ],
};
